import { useEffect, useState } from "react";
import { Users, Copy, Check, MessageCircle, Send, Instagram, Facebook, Mail, Share2, MessagesSquare, Sparkles } from "lucide-react";
import { getReferralInfo } from "@/lib/api";
import { toast } from "sonner";

export const InviteCard = () => {
  const [info, setInfo] = useState(null);
  const [copied, setCopied] = useState(false);
  const [canNativeShare, setCanNativeShare] = useState(false);

  useEffect(() => {
    getReferralInfo().then(setInfo).catch(() => setInfo(null));
    if (typeof navigator !== "undefined" && typeof navigator.share === "function") {
      setCanNativeShare(true);
    }
  }, []);

  if (!info) return null;

  const shareLink = `${window.location.origin}/join/${info.code}`;
  const emailSubject = "I found a study tool you should try — free with my link";
  const shareText = `I've been using Lectura — you name any book + chapter and it makes a full narrated lecture, slides, printable study guide and a graded quiz. Join with my link and you get 7 free lectures instead of 3 🎁\n\n${shareLink}`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(shareLink);
      setCopied(true);
      toast.success("Link copied — paste it anywhere");
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error("Couldn't copy — long-press the link to copy it manually");
    }
  };

  const share = async (target) => {
    const encText = encodeURIComponent(shareText);
    const encUrl = encodeURIComponent(shareLink);
    const encSubject = encodeURIComponent(emailSubject);

    if (target === "more") {
      // Native OS share sheet — best surface for Messenger, iMessage, Snap, email, etc.
      if (canNativeShare) {
        try {
          await navigator.share({ title: "Lectura", text: shareText, url: shareLink });
          return;
        } catch { /* user cancelled */ return; }
      }
      copyLink();
      toast.message("Link copied — paste it in Messenger, Snapchat, or wherever you chat.");
      return;
    }

    if (target === "instagram") {
      copyLink();
      toast.message("Instagram doesn't accept pre-filled text — link copied so you can paste it into a Story or DM.");
      window.open("https://www.instagram.com/", "_blank");
      return;
    }

    const links = {
      sms:      `sms:?&body=${encText}`,
      email:    `mailto:?subject=${encSubject}&body=${encText}`,
      whatsapp: `https://wa.me/?text=${encText}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encUrl}&quote=${encText}`,
      reddit:   `https://www.reddit.com/submit?title=${encodeURIComponent("Lectura — one AI study tool that actually gives you the whole package")}&text=${encText}&type=TEXT`,
    };
    window.open(links[target], target === "sms" || target === "email" ? "_self" : "_blank");
  };

  const capReached = info.cap_reached;
  const progress = Math.min(100, Math.round((info.completed / info.cap) * 100));

  return (
    <div data-testid="invite-card" className="rounded-2xl border border-border bg-card p-6">
      <div className="flex items-start gap-3">
        <span className="grid place-items-center h-10 w-10 rounded-xl bg-accent/15 text-accent shrink-0">
          <Users className="h-5 w-5" />
        </span>
        <div className="flex-1">
          <h3 className="font-serif text-xl font-bold">Invite friends, earn Pro</h3>
          <p className="mt-1 text-sm text-muted-foreground">
            Every friend who signs up and makes their first lecture unlocks{" "}
            <span className="font-semibold text-foreground">30 days of Pro</span> for you. They get 7 free lectures too.
          </p>
        </div>
      </div>

      <div className="mt-5 flex items-stretch gap-2">
        <div className="flex-1 rounded-xl border border-input bg-background px-4 py-3 text-sm font-mono truncate select-all" data-testid="invite-link">
          {shareLink}
        </div>
        <button
          data-testid="invite-copy-btn"
          onClick={copyLink}
          className="rounded-xl bg-primary text-primary-foreground px-4 font-semibold hover:opacity-90 transition flex items-center gap-2"
        >
          {copied ? <><Check className="h-4 w-4" /> Copied</> : <><Copy className="h-4 w-4" /> Copy</>}
        </button>
      </div>

      <div className="mt-3 grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-7 gap-2">
        <button data-testid="share-sms" onClick={() => share("sms")}
          className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition">
          <MessageCircle className="h-4 w-4" /> Text
        </button>
        <button data-testid="share-email" onClick={() => share("email")}
          className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition">
          <Mail className="h-4 w-4" /> Email
        </button>
        <button data-testid="share-whatsapp" onClick={() => share("whatsapp")}
          className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition">
          <Send className="h-4 w-4" /> WhatsApp
        </button>
        <button data-testid="share-facebook" onClick={() => share("facebook")}
          className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition">
          <Facebook className="h-4 w-4" /> Facebook
        </button>
        <button data-testid="share-instagram" onClick={() => share("instagram")}
          className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition">
          <Instagram className="h-4 w-4" /> Instagram
        </button>
        <button data-testid="share-reddit" onClick={() => share("reddit")}
          className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition">
          <MessagesSquare className="h-4 w-4" /> Reddit
        </button>
        <button data-testid="share-more" onClick={() => share("more")}
          title={canNativeShare ? "Native share sheet — Messenger, Snapchat, iMessage, etc." : "Copies the link so you can paste it in Messenger, Snapchat, or anywhere else"}
          className="flex items-center justify-center gap-1.5 rounded-xl border-2 border-border py-2.5 text-sm font-semibold hover:bg-secondary/60 transition">
          <Share2 className="h-4 w-4" /> More…
        </button>
      </div>

      <div className="mt-5 rounded-xl bg-secondary/50 p-4">
        <div className="flex items-center justify-between text-sm">
          <span className="text-muted-foreground">Your progress</span>
          <span className="font-semibold" data-testid="invite-progress-label">
            {info.completed} / {info.cap} friends {capReached ? "(cap reached)" : ""}
          </span>
        </div>
        <div className="mt-2 h-2 rounded-full bg-border overflow-hidden">
          <div className="h-full bg-accent transition-all" style={{ width: `${progress}%` }} />
        </div>
        <p className="mt-3 flex items-center gap-1.5 text-sm">
          <Sparkles className="h-4 w-4 text-accent" />
          <span data-testid="invite-days-earned">
            {info.days_earned > 0
              ? `You've earned ${info.days_earned} days of Pro so far`
              : "Invite 1 friend to earn your first 30 days free"}
          </span>
        </p>
        {info.pending > 0 && !capReached && (
          <p className="mt-1 text-xs text-muted-foreground">
            {info.pending} friend{info.pending === 1 ? "" : "s"} signed up — reward unlocks when they make their first lecture.
          </p>
        )}
        {info.invited_by_pending && (
          <p className="mt-3 rounded-lg border border-accent/40 bg-accent/10 px-3 py-2 text-xs">
            🎁 A friend invited you — make your first lecture to unlock their reward.
          </p>
        )}
      </div>
    </div>
  );
};
